import { useState } from "react";
import { Link } from "react-router-dom";
import api, { fileUrl, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/lib/seo";
import { toast } from "sonner";
import { MagnifyingGlass, UserCircle, MegaphoneSimple, CircleNotch, ArrowRight } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

export default function FindTeacher() {
  useSeo("Find My Teacher", "Search for a teacher or staff member you remember — or post a request and let the community help.");
  const { user } = useAuth();
  const [f, setF] = useState({ institution: "", years: "", subject: "", name: "", nickname: "" });
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [posts, setPosts] = useState([]);
  const [posting, setPosting] = useState(false);
  const [clues, setClues] = useState("");

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const search = async (e) => {
    e?.preventDefault();
    if (!f.institution && !f.subject && !f.name && !f.nickname) { toast.error("Add at least one clue"); return; }
    setBusy(true);
    try {
      const { data } = await api.get("/find-teacher", { params: f });
      setResults(data.people || []);
      const lf = await api.get("/looking-for");
      setPosts(lf.data.posts || []);
    } catch (e) { toast.error("Search failed"); } finally { setBusy(false); }
  };

  const postLooking = async () => {
    if (!user) { toast.error("Sign in to post a request"); return; }
    if (!f.institution.trim()) { toast.error("Please enter the school/college name"); return; }
    setPosting(true);
    try {
      await api.post("/looking-for", { institution_name: f.institution, years: f.years, subject: f.subject, name_hint: f.name, nickname: f.nickname, clues });
      toast.success("Posted — the community can now help you find them.");
      setClues("");
      const lf = await api.get("/looking-for"); setPosts(lf.data.posts || []);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setPosting(false); }
  };

  return (
    <div className="mx-auto max-w-4xl px-5 py-12" data-testid="find-teacher-page">
      <p className="overline text-secondary mb-3">People & Legacy</p>
      <h1 className="font-heading text-4xl font-light tracking-tight">Find my teacher</h1>
      <p className="mt-3 max-w-2xl text-muted-foreground">Remember a teacher or staff member but not sure where to look? Tell us what you recall and we'll search — if there's no match, post a request and the community can help.</p>

      <form onSubmit={search} className="mt-8 grid gap-3 rounded-2xl border border-border/60 bg-white p-5 shadow-sm sm:grid-cols-2" data-testid="find-teacher-form">
        <div><Label className="text-xs">School or college</Label><Input value={f.institution} onChange={(e) => set("institution", e.target.value)} className="mt-1" placeholder="e.g. St George L P S Elivali" data-testid="find-institution" /></div>
        <div><Label className="text-xs">Years you attended</Label><Input value={f.years} onChange={(e) => set("years", e.target.value)} className="mt-1" placeholder="e.g. 1978" data-testid="find-years" /></div>
        <div><Label className="text-xs">Subject they taught</Label><Input value={f.subject} onChange={(e) => set("subject", e.target.value)} className="mt-1" placeholder="e.g. Mathematics" data-testid="find-subject" /></div>
        <div><Label className="text-xs">Part of their name</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} className="mt-1" placeholder="e.g. Thomas" data-testid="find-name" /></div>
        <div><Label className="text-xs">Nickname (optional)</Label><Input value={f.nickname} onChange={(e) => set("nickname", e.target.value)} className="mt-1" data-testid="find-nickname" /></div>
        <div className="flex items-end">
          <Button type="submit" disabled={busy} className="w-full rounded-full gap-1.5" data-testid="find-submit">{busy ? <CircleNotch size={16} className="animate-spin" /> : <MagnifyingGlass size={16} />} {busy ? "Searching…" : "Search"}</Button>
        </div>
      </form>

      {results !== null && (
        <div className="mt-8" data-testid="find-results">
          <h2 className="font-heading text-xl font-medium">{results.length} match{results.length === 1 ? "" : "es"}</h2>
          {results.length > 0 ? (
            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {results.map((p) => (
                <Link key={p.id} to={`/person/${p.id}`} className="flex items-start gap-3 rounded-xl border border-slate-100 bg-white p-3 shadow-sm transition-all hover:-translate-y-0.5 hover:border-green-300 hover:shadow-md" data-testid={`find-result-${p.id}`}>
                  <span className="grid h-10 w-10 shrink-0 place-items-center overflow-hidden rounded-lg bg-green-50">{p.photo_url ? <img src={p.photo_url} alt="" className="h-full w-full object-cover" /> : <UserCircle size={22} weight="duotone" className="text-green-600" />}</span>
                  <span className="min-w-0"><span className="block text-sm font-medium text-foreground">{p.name}</span><span className="block truncate text-xs text-muted-foreground">{p.title || p.subcategory}{p.employments?.[0] ? ` · ${p.employments[0].institution_name}` : ""}</span></span>
                </Link>
              ))}
            </div>
          ) : (
            <div className="mt-4 rounded-2xl border border-secondary/30 bg-secondary/5 p-5" data-testid="no-match-block">
              <p className="flex items-center gap-2 font-medium"><MegaphoneSimple size={18} className="text-secondary" /> No match found — post a "Looking for my teacher" request</p>
              <p className="mt-1 text-sm text-muted-foreground">Other alumni and staff can help identify them. We'll use the clues above.</p>
              <Textarea rows={2} value={clues} onChange={(e) => setClues(e.target.value)} placeholder="Any other clues? Appearance, memorable habits, class taught…" className="mt-3" data-testid="looking-clues" />
              <Button onClick={postLooking} disabled={posting} className="mt-3 rounded-full gap-1.5" data-testid="post-looking-btn">{posting ? <CircleNotch size={15} className="animate-spin" /> : <MegaphoneSimple size={15} />} {posting ? "Posting…" : "Post request"}</Button>
            </div>
          )}
        </div>
      )}

      {posts.length > 0 && (
        <div className="mt-10" data-testid="looking-for-list">
          <h2 className="font-heading text-xl font-medium">Recent "looking for" requests</h2>
          <div className="mt-4 space-y-3">
            {posts.map((p) => (
              <div key={p.id} className="rounded-2xl border border-border/60 bg-white p-4" data-testid={`looking-post-${p.id}`}>
                <p className="text-sm"><span className="font-semibold">{p.author_name}</span> is looking for a {p.subject || "teacher"} at <span className="font-medium">{p.institution_name}</span>{p.years ? ` (${p.years})` : ""}.</p>
                {(p.name_hint || p.nickname) && <p className="text-xs text-muted-foreground">Name clue: {[p.name_hint, p.nickname].filter(Boolean).join(" / ")}</p>}
                {p.clues && <p className="mt-1 text-sm text-muted-foreground">{p.clues}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
